import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { fromLonLat, transform } from 'ol/proj';
import { Fill, Stroke, Style } from 'ol/style';
import Text from 'ol/style/Text';
import axiosInstance from '../axiosIntance';

interface BiensOffMarketLoyerM2LayerOptions {
  visible: boolean;
  labelMinZoom?: number;
}

// Must match createBiensOffMarketLayer dot dimensions
const DOT_RADIUS = 5;
const DOT_STROKE_WIDTH = 2;
const DOT_OUTER_RADIUS = DOT_RADIUS + DOT_STROKE_WIDTH / 2;
const LABEL_GAP = 3;

const formatLoyerM2Label = (value: number): string => {
  const formatted = Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${formatted}/m2/an`;
};

const selectLatestByLocation = (items: any[]): any[] => {
  const byLocation = new Map<string, any>();

  items.forEach((item) => {
    if (item.Loyer_par_m2 == null) return;
    if (!item.Position_sur_map?.coordinates) return;

    const key = item.Position_sur_map.coordinates.join(',');
    const existing = byLocation.get(key);
    if (!existing || item.date_created > existing.date_created) {
      byLocation.set(key, item);
    }
  });

  return Array.from(byLocation.values());
};

const createLabelStyle = (labelMinZoom: number) => (feature: any, resolution: number) => {
  const maxResolution = 156543.03392804097 / Math.pow(2, labelMinZoom);
  if (resolution >= maxResolution) {
    return undefined;
  }

  const labelText = feature.get('label');
  if (!labelText) return undefined;

  return new Style({
    text: new Text({
      text: labelText,
      font: 'bold 11px sans-serif',
      fill: new Fill({ color: '#333333' }),
      stroke: new Stroke({ color: '#ffffff', width: 3 }),
      backgroundFill: new Fill({ color: 'rgba(255, 255, 255, 0.92)' }),
      backgroundStroke: new Stroke({ color: 'rgba(0, 0, 0, 0.15)', width: 1 }),
      padding: [3, 5, 3, 5],
      textAlign: 'left',
      textBaseline: 'bottom',
      offsetX: DOT_OUTER_RADIUS + LABEL_GAP,
      offsetY: -(DOT_OUTER_RADIUS + LABEL_GAP),
      overflow: true,
    }),
  });
};

export function createBiensOffMarketLoyerM2Layer(options: BiensOffMarketLoyerM2LayerOptions) {
  const { visible, labelMinZoom = 16 } = options;
  const source = new VectorSource({ features: [] as any[] });

  const layer = new VectorLayer({
    source,
    visible,
    properties: { name: 'biens_off_market_loyer_m2' },
    style: createLabelStyle(labelMinZoom),
    zIndex: 12,
  });

  const updateFromItems = (items: any[]) => {
    if (!layer.getVisible()) return;

    source.clear();

    selectLatestByLocation(items).forEach((item) => {
      const [lon, lat] = item.Position_sur_map.coordinates;
      const feature = new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
        label: formatLoyerM2Label(item.Loyer_par_m2),
        name: item.NoParcelle || item.id,
      });
      source.addFeature(feature as any);
    });
  };

  const refreshData = async (mapInstance: any, currentFilters?: any) => {
    try {
      if (!layer.getVisible()) return;

      const view = mapInstance.getView();
      const extent = view.calculateExtent(mapInstance.getSize());
      const [minX, minY, maxX, maxY] = extent;

      const [minLon, minLat] = transform([minX, minY], 'EPSG:3857', 'EPSG:4326');
      const [maxLon, maxLat] = transform([maxX, maxY], 'EPSG:3857', 'EPSG:4326');

      let filterString = '';
      if (currentFilters && Object.keys(currentFilters).length > 0) {
        filterString = `&filter=${encodeURIComponent(JSON.stringify(currentFilters))}`;
      } else {
        filterString = '&filter={"_and":[{"Position_sur_map":{"_nnull":true}},{"status":{"_in":["actif","statistique"]}}]}';
      }

      const response = await axiosInstance.get(
        `https://melcarnedirectus2.digipulse.ch/items/biens_off_market?fields=*${filterString}&limit=1000`
      );

      const items = response.data.data.filter((item: any) => {
        if (!item.Position_sur_map?.coordinates) return false;
        const [lon, lat] = item.Position_sur_map.coordinates;
        return lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat;
      });

      updateFromItems(items);
      layer.setVisible(true);
    } catch (error) {
      console.error('Error fetching biens off-market loyer/m2 data:', error);
    }
  };

  (layer as any)._refreshData = refreshData;
  (layer as any)._updateFromItems = updateFromItems;

  return layer;
}
